/**
 * Chess engine.
 *
 * Backed by chess.js for legal-move generation, check/checkmate/
 * stalemate detection, and FEN serialisation. The wire-format state
 * is just `{ fen, history, turnIndex, players, terminal }` — the
 * library state is reconstructed each call from the FEN, so moves
 * remain deterministic round-tripping through MLS.
 *
 * Pawn promotion auto-selects queen for simplicity.
 */
import { Chess, type Move, type Square } from 'chess.js';
import { useState } from 'react';
import type { UserId } from '@chat/protocol';
import type { GameComponentProps, GameEngine, GameTerminal } from './engine.js';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export interface ChessState {
  /** FEN of the current position. */
  readonly fen: string;
  /** SAN history of moves played so far. */
  readonly history: readonly string[];
  /** 0 = white-to-move (players[0]), 1 = black-to-move (players[1]). */
  readonly turnIndex: number;
  readonly players: readonly UserId[];
  readonly terminal: GameTerminal | null;
}

export interface ChessMove {
  readonly from: string;
  readonly to: string;
  /** 'q' | 'r' | 'b' | 'n' — defaults to queen client-side. */
  readonly promotion?: 'q' | 'r' | 'b' | 'n';
}

function loadChess(fen: string): Chess {
  return new Chess(fen);
}

function evaluate(chess: Chess, players: readonly UserId[]): GameTerminal | null {
  if (chess.isCheckmate()) {
    // The side TO MOVE has just been mated → the OTHER side wins.
    const loserIdx = chess.turn() === 'w' ? 0 : 1;
    const winner = players[1 - loserIdx];
    if (winner) return { winner };
    return null;
  }
  if (chess.isDraw() || chess.isStalemate() || chess.isThreefoldRepetition()) {
    return { draw: true };
  }
  return null;
}

export const chess: GameEngine<ChessState, ChessMove> = {
  id: 'chess',
  displayName: 'Chess',
  minPlayers: 2,
  maxPlayers: 2,

  initialState(players) {
    if (players.length < 2) throw new Error('chess needs two players');
    return {
      fen: STARTING_FEN,
      history: [],
      turnIndex: 0,
      players: players.slice(0, 2),
      terminal: null,
    };
  },

  validateMove(state, move, by) {
    if (state.terminal) return 'game is over';
    const playerIndex = state.players.indexOf(by);
    if (playerIndex < 0) return 'spectator — not your game';
    if (playerIndex !== state.turnIndex) return 'not your turn';
    if (typeof move.from !== 'string' || typeof move.to !== 'string') return 'invalid move';
    const game = loadChess(state.fen);
    try {
      const result = game.move({
        from: move.from,
        to: move.to,
        promotion: move.promotion ?? 'q',
      });
      if (!result) return 'illegal move';
    } catch {
      return 'illegal move';
    }
    return null;
  },

  applyMove(state, move, _by) {
    const game = loadChess(state.fen);
    const result = game.move({
      from: move.from,
      to: move.to,
      promotion: move.promotion ?? 'q',
    });
    if (!result) {
      // validateMove should have caught this, but bail safely.
      return state;
    }
    const terminal = evaluate(game, state.players);
    return {
      ...state,
      fen: game.fen(),
      history: [...state.history, result.san],
      turnIndex: terminal ? state.turnIndex : 1 - state.turnIndex,
      terminal,
    };
  },

  currentTurn(state) {
    if (state.terminal) return null;
    return state.players[state.turnIndex] ?? null;
  },

  terminal(state) {
    return state.terminal;
  },

  isPlayer(state, user) {
    return state.players.includes(user);
  },

  Component: ChessBoard,
};

const PIECE_GLYPHS: Record<string, string> = {
  p: '♟',
  r: '♜',
  n: '♞',
  b: '♝',
  q: '♛',
  k: '♚',
  P: '♙',
  R: '♖',
  N: '♘',
  B: '♗',
  Q: '♕',
  K: '♔',
};

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const RANKS = ['8', '7', '6', '5', '4', '3', '2', '1'] as const;

function squareName(fileIdx: number, rankIdx: number): string {
  return `${FILES[fileIdx] ?? 'a'}${RANKS[rankIdx] ?? '1'}`;
}

function ChessBoard({ state, self, onMove, disabled }: GameComponentProps<ChessState, ChessMove>) {
  const myIndex = state.players.indexOf(self);
  const spectator = myIndex < 0;
  const canMove = !disabled && !spectator && !state.terminal && state.turnIndex === myIndex;
  const game = loadChess(state.fen);
  const board = game.board(); // 8x8, index 0 is rank 8 (top), col 0 is file a (left)

  // Tracks which square the player has clicked first; the second
  // click attempts a move there. Cleared after a successful move
  // or on click-off.
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const legalDestinations = (() => {
    if (!selected) return new Set<string>();
    try {
      const moves: Move[] = game.moves({ square: selected as Square, verbose: true });
      return new Set(moves.map((m) => m.to));
    } catch {
      return new Set<string>();
    }
  })();

  const turnLabel = state.terminal
    ? state.terminal.draw
      ? 'draw'
      : state.terminal.winner === self
        ? 'you won'
        : 'you lost'
    : spectator
      ? 'spectating'
      : canMove
        ? game.inCheck()
          ? 'your turn — in check!'
          : 'your turn'
        : 'waiting for opponent';

  function squareClick(file: number, rank: number, occupant: { color: 'w' | 'b' } | null): void {
    if (!canMove) return;
    const sq = squareName(file, rank);
    if (selected) {
      if (sq === selected) {
        setSelected(undefined);
        return;
      }
      if (legalDestinations.has(sq)) {
        onMove({ from: selected, to: sq });
        setSelected(undefined);
        return;
      }
      // Selecting a different friendly piece — switch selection.
      const myColor: 'w' | 'b' = state.turnIndex === 0 ? 'w' : 'b';
      if (occupant && occupant.color === myColor) {
        setSelected(sq);
        return;
      }
      setSelected(undefined);
      return;
    }
    // First click: only meaningful on a friendly piece.
    const myColor: 'w' | 'b' = state.turnIndex === 0 ? 'w' : 'b';
    if (occupant && occupant.color === myColor) setSelected(sq);
  }

  // Flip the board for black so the player always sees their pieces
  // on the bottom — chess.js's board() is white-on-bottom by default;
  // when black, we present rank-iteration in reverse.
  const playingBlack = !spectator && myIndex === 1;
  const rankOrder = playingBlack ? [...board].reverse() : board;
  const rankIndices = playingBlack ? [...board.keys()].reverse() : [...board.keys()];
  const fileOrder = playingBlack ? [...FILES].reverse() : [...FILES];
  const fileIndices = playingBlack ? [...FILES.keys()].reverse() : [...FILES.keys()];

  return (
    <section data-testid="chess-board" className="chess-board-wrap">
      <p data-testid="chess-status">{turnLabel}</p>
      <div className="chess-board" role="grid">
        {rankOrder.map((row, displayRank) => {
          const realRankIdx = rankIndices[displayRank] ?? displayRank;
          return (
            <div className="chess-rank" role="row" key={`r-${String(realRankIdx)}`}>
              {fileIndices.map((realFileIdx, displayFile) => {
                const cell = row[realFileIdx] ?? null;
                const sq = squareName(realFileIdx, realRankIdx);
                const isLight = (realFileIdx + realRankIdx) % 2 === 0;
                const isSelected = sq === selected;
                const isTargetable = legalDestinations.has(sq);
                const piece = cell
                  ? PIECE_GLYPHS[cell.color === 'w' ? cell.type.toUpperCase() : cell.type]
                  : '';
                return (
                  <button
                    key={sq}
                    type="button"
                    role="gridcell"
                    data-testid={`chess-sq-${sq}`}
                    data-square={sq}
                    className={`chess-cell ${isLight ? 'light' : 'dark'}${
                      isSelected ? ' selected' : ''
                    }${isTargetable ? ' target' : ''}`}
                    disabled={!canMove}
                    onClick={() => {
                      squareClick(realFileIdx, realRankIdx, cell);
                    }}
                    aria-label={`${sq}${piece ? ` ${piece}` : ''}`}
                  >
                    <span className="chess-piece">{piece}</span>
                    {displayRank === rankOrder.length - 1 && (
                      <span className="chess-coord file">{fileOrder[displayFile]}</span>
                    )}
                    {displayFile === 0 && (
                      <span className="chess-coord rank">
                        {playingBlack ? RANKS[RANKS.length - 1 - realRankIdx] : RANKS[realRankIdx]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </section>
  );
}
